import React, { useEffect, useMemo, useReducer } from "react";
import { formatCurrency, getClientType } from "../../utils";
import { CheckoutApiClient } from "../api/CheckoutApiClient";

export const CheckoutContext = React.createContext();

export const ACTION_START_LOADING = "startLoading";
export const ACTION_STOP_LOADING = "stopLoading";
export const ACTION_INIT_CHECKOUT = "init";
export const ACTION_UPDATE_CART = "updateCart";
export const ACTION_UPDATE_DELIVERY_METHODS = "updateDeliveryMethods";
export const ACTION_SET_DELIVERY_OPTION = "set-delivery-option";
export const ACTION_START_LOADING_PAYMENT_OPTIONS =
  "startLoadingPaymentOptions";
export const ACTION_STOP_LOADING_PAYMENT_OPTIONS = "stopLoadingPaymentOptions";
export const ACTION_SET_PAYMENT_OPTIONS = "setPaymentOptions";
export const ACTION_SET_PAYMENT_OPTION = "setPaymentOption";
export const ACTION_SET_TRANSACTION = "setTransaction";
export const ACTION_SET_ORDER_COMMENT = "setOrderComment";
export const ACTION_SET_CURRENT_STEP = "setCurrentStep";
export const ACTION_SET_CREATING_TRANSACTION = "setCreatingTransaction";
export const ACTION_SET_ERROR_CREATING_TRANSACTION =
  "setErrorCreatingTransaction";

const triggerCartUpdateEvent = (cart) => {
  const event = jQuery.Event("shoppingcart:update");
  event.items = cart.orderLines
    .filter((ol) => ol.type == 0)
    .reduce((total, ol) => total + ol.quantity, 0);
  // Ensure that total price have two decimals
  event.totalCost = Math.round(cart.summary.totalPrice * 100) / 100;
  event.totalSum = formatCurrency(event.totalCost);
  $(document).trigger(event);
};

const checkoutReducer = (state, action) => {
  console.log("[Checkout] Got action", action);
  switch (action.type) {
    case ACTION_INIT_CHECKOUT:
      // Fire for legacy code:
      if (typeof jQuery != "undefined") {
        triggerCartUpdateEvent(action.cart);
      }
      return {
        ...state,
        isLoading: false,
        cart: action.cart,
        checkoutUuid: action.checkoutUuid,
        deliveryMethods: action.deliveryMethods,
        user: action.user,
        hasActiveRebateCampaigns: action.hasActiveRebateCampaigns,
        options: action.options,
        upsaleList: action.upsaleList,
        termsOfPurchase: action.termsOfPurchase,
        catalogueMaxDeliveryTime: action.catalogueMaxDeliveryTime,
      };
    case ACTION_START_LOADING:
      return {
        ...state,
        isLoading: true,
      };
    case ACTION_STOP_LOADING:
      return {
        ...state,
        isLoading: false,
      };
    case ACTION_SET_CURRENT_STEP:
      return {
        ...state,
        currentStep: action.currentStep,
      };
    case ACTION_START_LOADING_PAYMENT_OPTIONS:
      return {
        ...state,
        isLoadingPaymentOptions: true,
      };
    case ACTION_STOP_LOADING_PAYMENT_OPTIONS:
      return {
        ...state,
        isLoadingPaymentOptions: false,
      };
    case ACTION_SET_PAYMENT_OPTIONS:
      console.log("Set payment options action", action);
      return {
        ...state,
        isLoadingPaymentOptions: false,
        paymentOptions: action.paymentOptions,
        cart: action.cart,
      };
    case ACTION_SET_PAYMENT_OPTION:
      return {
        ...state,
        paymentOption: action.paymentOption,
        paymentOptionData: action.paymentOptionData,
        currentStep: "confirm-order",
      };
    case ACTION_SET_TRANSACTION:
      return {
        ...state,
        transaction: action.transaction,
      };
    case ACTION_SET_DELIVERY_OPTION:
      return {
        ...state,
        // To reload, we have to get make a new object?
        deliveryOption: {
          ...action.deliveryOption,
        },
        deliveryOptionData: action.deliveryOptionData,
        paymentOptions: null,
        //paymentOption: null,
        currentStep: "select-payment-option",
      };
    case ACTION_SET_CREATING_TRANSACTION:
      return {
        ...state,
        createTransactionError: null,
        isCreatingTransaction: true,
      };
    case ACTION_SET_ERROR_CREATING_TRANSACTION:
      return {
        ...state,
        createTransactionError: action.error,
        isCreatingTransaction: false,
      };
    case ACTION_UPDATE_CART:
      // Fire for legacy code:
      if (typeof jQuery != "undefined") {
        triggerCartUpdateEvent(action.cart);
      }
      console.log("update cart", action);
      return {
        ...state,
        catalogueMaxDeliveryTime: action.catalogueMaxDeliveryTime,
        cart: action.cart,
      };
    case ACTION_UPDATE_DELIVERY_METHODS:
      return {
        ...state,
        deliveryMethods: action.deliveryMethods,
      };
    case ACTION_SET_ORDER_COMMENT:
      return {
        ...state,
        orderComment: action.orderComment,
      };
    default:
      console.log("Unknown action", action);
      return state;
  }
};

const initialState = {
  isLoading: true,
  cart: null,
  checkoutUuid: null,
  client: null,
  deliveryMethods: null,
  deliveryOption: null,
  deliveryOptionData: null,
  isLoadingPaymentOptions: false,
  isCreatingTransaction: false,
  createTransactionError: null,
  paymentOptions: null,
  paymentOption: null,
  transaction: null,
  catalogueMaxDeliveryTime: null,
  hasActiveRebateCampaigns: null,
  user: null,
  termsOfPurchase: "",
  orderComment: "",
  options: {},
  currentStep: "select-delivery-option",
};

const CheckoutProvider = ({
  children,
  apiClient: apiClientProp,
  client = "",
  type = "WEBSHOP",
}) => {
  const [state, dispatch] = useReducer(checkoutReducer, {
    ...initialState,
    client,
  });

  const apiClient = useMemo(() => {
    if (apiClientProp) {
      return apiClientProp;
    }

    return new CheckoutApiClient();
  }, [apiClientProp]);

  // Create cart:
  useEffect(() => {
    console.log("[CheckoutProvider]", "Initializing");

    apiClient
      .create()
      .then((json) => {
        console.log("Got json data", json);

        dispatch({
          type: ACTION_INIT_CHECKOUT,
          cart: json.cart,
          checkoutUuid: json.checkoutUuid,
          deliveryMethods: json.deliveryOptions,
          user: json.user,
          hasActiveRebateCampaigns: json.hasActiveRebateCampaigns,
          termsOfPurchase: json.termsOfPurchase,
          options: json.options,
          upsaleList: json.upsaleList,
          catalogueMaxDeliveryTime: json.catalogueMaxDeliveryTime,
        });

        // The checkout pages do not mount PictureSetsProvider, which is where
        // the arms are normally reported from, and this request can assign an
        // arm of its own that earlier pages could not have carried over.
        if (
          json.experimentArms &&
          window.isEpmsAnalyticsFeatureEnabled &&
          window.epmsAnalytics
        ) {
          window.epmsAnalytics.setExperimentArms(json.experimentArms);
        }
      })
      .finally((e) => {
        console.log("Finally data", e);
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, []);

  useEffect(() => {
    if (!state.deliveryOption) {
      dispatch({
        type: ACTION_SET_PAYMENT_OPTIONS,
        paymentOptions: [],
        cart: state.cart,
      });
      return;
    }

    dispatch({
      type: ACTION_START_LOADING_PAYMENT_OPTIONS,
    });

    apiClient
      .getPaymentOptionsByDeliveryOptionUuid(
        state.checkoutUuid,
        state.deliveryOption.uuid,
      )
      .then((json) => {
        console.log("Got payment options", json);
        dispatch({
          type: ACTION_SET_PAYMENT_OPTIONS,
          paymentOptions: json.paymentOptions,
          cart: json.cart,
        });
      });
  }, [state.deliveryOption]);

  useEffect(() => {
    // Only update prices if we change to an option. Is this the behaviour we want?
    if (!state.paymentOption) {
      return;
    }

    console.log("Payment option changed to", state.paymentOption);
    apiClient
      .setPaymentOptionUuid(
        state.checkoutUuid,
        state.deliveryOption.uuid,
        state.paymentOption.uuid,
      )
      .then((json) => {
        console.log("Got payment options", json);
        dispatch({
          type: ACTION_UPDATE_CART,
          cart: json.cart,
        });
      });
  }, [state.paymentOption]);

  const setDeliveryOption = (deliveryOption, deliveryOptionData) => {
    console.log("Setting delivery option", deliveryOption, deliveryOptionData);
    dispatch({
      type: ACTION_SET_DELIVERY_OPTION,
      deliveryOption,
      deliveryOptionData,
    });
  };

  const setPaymentOption = (paymentOption, data) => {
    console.log("Setting payment option", paymentOption);
    dispatch({
      type: ACTION_SET_PAYMENT_OPTION,
      paymentOption,
      paymentOptionData: data,
    });
  };

  const setOrderComment = (comment) => {
    dispatch({
      type: ACTION_SET_ORDER_COMMENT,
      orderComment: comment,
    });
  };

  const setRebateCode = (code) => {
    return new Promise((resolve, reject) => {
      console.log("Set rebate code", state);
      apiClient
        .useVoucherCode(state.checkoutUuid, code, state.deliveryOption?.uuid)
        .then((json) => {
          if (json.error) {
            reject(json.error);
            return;
          }
          dispatch({
            type: ACTION_UPDATE_CART,
            cart: json.cart,
          });
          resolve(true);
        })
        .catch((e) => {
          reject(e);
        });
    });
  };

  const setCurrentStep = (step) => {
    dispatch({
      type: ACTION_SET_CURRENT_STEP,
      currentStep: step,
    });
  };

  /**
   * Adds the complete digital portrait set for each of the given children. The
   * returned promise rejects when the request does, so callers can show an
   * error and re-enable their button.
   *
   * @param {{uuid: string, name: string}[]} subjects
   */
  const buyDigitalSets = (subjects) => {
    return apiClient
      .buyDigitalSets(state.checkoutUuid, subjects)
      .then((json) => {
        dispatch({
          type: ACTION_UPDATE_CART,
          cart: json.cart,
        });

        dispatch({
          type: ACTION_UPDATE_DELIVERY_METHODS,
          deliveryMethods: json.deliveryMethods,
        });

        if (window.isEpmsAnalyticsFeatureEnabled && window.epmsAnalytics) {
          window.epmsAnalytics.onCartChange(json);
        }

        return json;
      });
  };

  const buyCatalogues = (catalogues) => {
    return new Promise((resolve, reject) => {
      apiClient.buyCatalogues(state.checkoutUuid, catalogues).then((json) => {
        dispatch({
          type: ACTION_UPDATE_CART,
          cart: json.cart,
          catalogueMaxDeliveryTime: json.catalogueMaxDeliveryTime,
        });

        dispatch({
          type: ACTION_UPDATE_DELIVERY_METHODS,
          deliveryMethods: json.deliveryMethods,
        });

        resolve(json);
      });
    });
  };

  const createTransaction = (stateOverride = {}) => {
    const requestState = {
      ...state,
      ...stateOverride,
    };
    const data = createTransactionRequestData(requestState);
    const checkoutUuid = requestState.checkoutUuid ?? state.checkoutUuid;

    dispatch({
      type: ACTION_SET_CREATING_TRANSACTION,
    });

    return apiClient
      .createTransaction(checkoutUuid, data)
      .then((json) => {
        if (json.error) {
          dispatch({
            type: ACTION_SET_ERROR_CREATING_TRANSACTION,
            error: json.error,
          });
        } else {
          dispatch({
            type: ACTION_SET_TRANSACTION,
            transaction: {
              uuid: json.transaction.uuid,
              order: {
                uuid: json.order.uuid,
              },
            },
          });
        }

        return json;
      })
      .catch((err) => {
        dispatch({
          type: ACTION_SET_ERROR_CREATING_TRANSACTION,
          error: err.message,
        });

        throw err;
      });
  };

  const replaceCart = (cart) => {
    dispatch({
      type: ACTION_UPDATE_CART,
      cart,
      catalogueMaxDeliveryTime: state.catalogueMaxDeliveryTime,
    });
  };

  const updateDeliveryMethods = (deliveryMethods) => {
    dispatch({
      type: ACTION_UPDATE_DELIVERY_METHODS,
      deliveryMethods,
    });
  };

  console.log("The new state is", state);

  return (
    <CheckoutContext.Provider
      value={{
        isLoaded: !state.isLoading,
        cart: state.cart,
        checkoutUuid: state.checkoutUuid,
        client: state.client,
        deliveryMethods: state.deliveryMethods,
        isLoadingPaymentOptions: state.isLoadingPaymentOptions,
        paymentOptions: state.paymentOptions,
        paymentOption: state.paymentOption,
        paymentOptionData: state.paymentOptionData,
        deliveryOption: state.deliveryOption,
        deliveryOptionData: state.deliveryOptionData,
        state: state,
        apiClient,
        replaceCart,
        updateDeliveryMethods,
        setPaymentOption,
        setDeliveryOption,
        createTransaction,
        setRebateCode,
        setOrderComment,
        setCurrentStep,
        buyDigitalSets,
        buyCatalogues,
        transaction: state.transaction,
        hasActiveRebateCampaigns: state.hasActiveRebateCampaigns,
        user: state.user,
        options: state.options,
        termsOfPurchase: state.termsOfPurchase,
        orderComment: state.orderComment,
        currentStep: state.currentStep,
        isCreatingTransaction: state.isCreatingTransaction,
        upsaleList: state.upsaleList,
        createTransactionError: state.createTransactionError,
        catalogueMaxDeliveryTime: state.catalogueMaxDeliveryTime,
        type: type,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};

export default CheckoutProvider;

const createTransactionRequestData = (state) => {
  let client = "mobile";
  if (getClientType() == "desktop") {
    client = "desktop";
  }
  console.log("Client name is", client, state, state.client);
  if (state.client) {
    client = state.client;
  }
  return {
    deliveryOptionUuid: state.deliveryOption.uuid,
    paymentOptionUuid: state.paymentOption.uuid,
    deliveryOptionData: state.deliveryOptionData,
    paymentOptionData: state.paymentOptionData,
    client,
    comment: state.orderComment,
  };
};

export const useCheckout = () => React.useContext(CheckoutContext);
